// frontend/src/pages/auth/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CssBaseline,
  IconButton,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import syncroLogo from '../../assets/syncro_logo_color.svg';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const GradientBackground = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf9 100%)',
  padding: 16,
});

const StyledPaper = styled(Paper)({
  padding: 32,
  borderRadius: 20,
  maxWidth: 460,
  width: '100%',
  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.12)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
});

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    license_key: ''
  });

  const [showLicenseField, setShowLicenseField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('verified') === '1') {
      setVerified(true);
      setShowLicenseField(true);
      const timer = setTimeout(() => setVerified(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email" && !searchParams.get('verified')) {
      setShowLicenseField(false);
      setFormData(prev => ({ ...prev, license_key: "" }));
    }

    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setVerified(false);

    try {
      const result = await login(formData.email, formData.password, formData.license_key);

      if (!result.success) {
        setError(result.error);
        if (result.error?.toLowerCase().includes('license')) {
          setShowLicenseField(true);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GradientBackground>
      <CssBaseline />
      <StyledPaper elevation={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          {/* Updated Logo */}
          <Box
            component="img"
            src={syncroLogo}
            alt="Syncro Logo"
            sx={{
              height: 64,
              objectFit: 'contain'
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Project Management Platform
          </Typography>
        </Box>

        {verified && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            ✅ Email verified! Please log in with your temporary password.
            {showLicenseField && " Enter your license key below."}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {showLicenseField && (
            <TextField
              margin="normal"
              required
              fullWidth
              name="license_key"
              label="License Key (First time only)"
              id="license_key"
              placeholder="Enter your license key"
              value={formData.license_key}
              onChange={handleChange}
              autoFocus
            />
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              background: 'linear-gradient(135deg, #4361ee, #3a0ca3)',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #3a50e2, #2d0a9a)',
                boxShadow: '0 4px 12px rgba(67, 97, 238, 0.3)',
              },
            }}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link to="/register" variant="body2" color="primary">
              Don't have an account? Register Company
            </Link>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Link to="/forgot-password" variant="body2" color="text.secondary">
              Forgot your password?
            </Link>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
          <Typography variant="body2" color="text.secondary">
            Designed & Developed by{' '}
            <a
              href="https://www.softsolutionmedia.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4361ee', textDecoration: 'none' }}
            >
              Soft Solution Media
            </a>
          </Typography>
        </Box>
      </StyledPaper>
    </GradientBackground>
  );
};

export default LoginPage;